/** 39-section Compliance Audit panel — real-time inspection of live project data. */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Warning, XCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getComplianceAudit } from "@/lib/api";


const TONE = {
  Implemented:            "bg-emerald-100 text-emerald-800 border-emerald-300",
  Passed:                 "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Partially Implemented": "bg-amber-100 text-amber-800 border-amber-300",
  "UI Only":              "bg-amber-100 text-amber-800 border-amber-300",
  "Backend Only":         "bg-sky-100 text-sky-800 border-sky-300",
  "Not Connected":        "bg-orange-100 text-orange-800 border-orange-300",
  "Not Implemented":      "bg-slate-100 text-slate-700 border-slate-300",
  "Requires Validation":  "bg-purple-100 text-purple-800 border-purple-300",
  Blocked:                "bg-red-100 text-red-800 border-red-300",
  Incorrect:              "bg-red-100 text-red-800 border-red-300",
};


export default function CPComplianceAuditPanel({ projectId }) {
  const q = useQuery({
    queryKey: ["compliance-audit", projectId],
    queryFn:  () => getComplianceAudit(projectId),
    enabled:  !!projectId,
  });

  const data = q.data;
  const groups = useMemo(() => {
    if (!data?.sections) return {};
    return data.sections.reduce((m, r) => {
      const k = r.module || "Other";
      (m[k] ??= []).push(r);
      return m;
    }, {});
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {data?.n_sections ?? 0} requirements inspected · {data?.summary?.passing_pct ?? 0}% passing
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => q.refetch()}
                    disabled={q.isFetching}
                    data-testid="w-audit-refresh">
          <ArrowsClockwise size={11} className={`mr-1 ${q.isFetching ? "animate-spin" : ""}`} />
          {q.isFetching ? "Refreshing…" : "Refresh audit"}
        </Button>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {Object.entries(data.summary.by_status).map(([status, n]) => (
            <div key={status} className={`rounded border p-2 text-center ${TONE[status] || "bg-slate-100 text-slate-700 border-slate-300"}`}
                  data-testid={`w-audit-status-${status.replaceAll(" ", "-").toLowerCase()}`}>
              <div className="text-[9px] uppercase tracking-widest font-mono opacity-70">{status}</div>
              <div className="text-lg font-bold">{n}</div>
            </div>
          ))}
        </div>
      )}

      {Object.entries(groups).map(([module, rows]) => (
        <div key={module} className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mt-3 mb-1">
            {module}
          </div>
          <div className="rounded border border-slate-200 overflow-hidden">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1 w-10">#</th>
                  <th className="text-left px-2 py-1">Requirement</th>
                  <th className="text-left px-2 py-1 w-40">Status</th>
                  <th className="text-left px-2 py-1 w-40">Evidence</th>
                  <th className="text-left px-2 py-1 w-16">Priority</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.section} className="border-t border-slate-200"
                        data-testid={`w-audit-section-${r.section}`}>
                    <td className="px-2 py-1 font-bold text-slate-500">{r.section}</td>
                    <td className="px-2 py-1">
                      <div className="text-slate-800">{r.requirement}</div>
                      {r.missing && r.missing !== "—" && (
                        <div className="text-[10px] text-amber-700 mt-0.5">
                          <Warning size={10} className="inline mr-1 -mt-0.5" />
                          <b>Missing:</b> {r.missing}
                        </div>
                      )}
                      {r.corrective_action && r.corrective_action !== "—" && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          <b>Next:</b> {r.corrective_action}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 rounded border ${TONE[r.status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                        {r.status === "Implemented" || r.status === "Passed" ? <CheckCircle size={10} className="inline mr-1 -mt-0.5" /> :
                          r.status === "Blocked" || r.status === "Incorrect" ? <XCircle size={10} className="inline mr-1 -mt-0.5" /> :
                          <Warning size={10} className="inline mr-1 -mt-0.5" />}
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-slate-500 text-[10px]">{r.evidence || "—"}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] font-bold ${r.priority === "P0" ? "text-red-600" : "text-amber-600"}`}>{r.priority}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
